import { EventEmitter } from "~/store/event";
import { Remote, wrap } from "comlink";
import type { MetricsWorker, MetricsWorkerOptions } from "./metricsWorker";
import { MetricsQuery } from "~/services/APIServices";

// Extend the EventEmitter with our worker events
export const metricWorkerEvents = new EventEmitter<{
  'worker:data': [ objectId : string, workerID : string, payload : Record<string, [number, number][]>]; // [objectID, workerID, payload]
  'worker:error': [ objectId : string, workerID : string, error : Error]; // [taskId, workerID, error]
}>();



/**
 * MetricsClient is a class that initializes a metrics worker and provides a method to update the metrics
 * 
 * @note This class is used to fetch metrics from the worker
 */
export class MetricsClient { 
  private worker?: Remote<MetricsWorker>;
  private instance?: Worker;

  /**
   * Initialize the metrics worker
   * @param type - The type of metrics to fetch
   * @param params - The parameters for the metrics query
   * @param token - The authentication token
   * @param updateCallback - The callback function to update the metrics
   * @param options - The options for the metrics worker
   */
  async initialize(type : 'radios' | 'devices' | 'edges', params: MetricsQuery, token: string, updateCallback: (id: string, metrics: Record<string, [number, number][]> ) => void, options: MetricsWorkerOptions = {}) {
    // Terminate any existing worker before creating a new one
    await this.terminate();
    
    this.instance = new Worker(new URL('./metricsWorker', import.meta.url), {
      type: 'module',
      name: `metrics-worker-${options.name || ''}-${type} (${params.metrics.join(',')})`
    });
    this.worker = wrap<MetricsWorker>(this.instance);
    this.instance.onmessage = (e) => {
      if (e.data?.type === 'update') {
        metricWorkerEvents.emit('worker:data', e.data.objectID, e.data.workerID, e.data.metrics);
        updateCallback(e.data.objectID, e.data.metrics);
      } else if (e.data?.type === 'error') {
        console.error('Metrics worker error:', e.data.error);
        metricWorkerEvents.emit('worker:error', e.data.objectID, e.data.workerID, e.data.error);
      }
    };

    this.instance.onerror = (error) => {
      console.error('Worker error:', error);
    };


    await this.worker.initialize(type, params, token, options);
  }

  async updateParams(type : 'radios' | 'devices' | 'edges', params: Partial<MetricsQuery>, token: string, options: MetricsWorkerOptions = {}) {
    if (!this.worker) {
      console.warn('Worker not initialized before updateParams call');
      return null;
    }
    return this.worker.updateParams(type, params, token, options);
  }

  getWorkerID() {
    return this.worker?.getWorkerID();
  }

  /**
   * Terminate the metrics worker
   */
  async terminate() {
    try {
      if (this.worker) {
        await this.worker.terminate();
        this.worker = undefined;
      }
      if (this.instance) {
        this.instance.terminate();
        this.instance = undefined;
      }
    } catch (error) {
      console.error('Error terminating metrics worker:', error);
    }
  }

  /**
   * Stop the metrics worker
   */
  stop() {
    try {
      this.worker?.stop();
    } catch (error) {
      console.error('Error stopping metrics worker:', error);
    }
  }
}
